import React from "react";
import {Alert, Descriptions} from "antd";
import {formatNaira} from "../../designSystem";

const amount = (value: number | undefined) => value == null ? "—" : formatNaira(value);

export type PaymentLinkBalance = {
  bookBalance: number;
  actualBalance: number;
  adminBalanceRefundAmount: number;
  pendingLiquidationAmount: number;
  liquidatedAmount: number;
  pendingRefundAmount: number;
  refundedAmount: number;
  balanceShortfall: number;
};

export function OwnerPaymentLinkBalance({balance}: {balance?: PaymentLinkBalance}) {
  if (!balance) return <Alert type="info" title="Balance totals are unavailable. Refresh after the API update."/>;
  return (
    <div>
      <Descriptions bordered size="small" column={{xs: 1, md: 2, xl: 4}}>
        <Descriptions.Item label="Book balance / paid collections">{amount(balance.bookBalance)}</Descriptions.Item>
        <Descriptions.Item label="Restored failed payments">{amount(balance.adminBalanceRefundAmount)}</Descriptions.Item>
        <Descriptions.Item label="Pending withdrawals">{amount(balance.pendingLiquidationAmount)}</Descriptions.Item>
        <Descriptions.Item label="Completed withdrawals">{amount(balance.liquidatedAmount)}</Descriptions.Item>
        <Descriptions.Item label="Pending refunds">{amount(balance.pendingRefundAmount)}</Descriptions.Item>
        <Descriptions.Item label="Completed refunds">{amount(balance.refundedAmount)}</Descriptions.Item>
        <Descriptions.Item label="Active balance / withdrawable">{amount(balance.actualBalance)}</Descriptions.Item>
      </Descriptions>
      <p className="mt-2 text-sm text-slate-500">
        Active balance = paid collections + restored payments − pending and completed withdrawals − pending and completed refunds.
        Includes deleted links and all payment history. Payer charges are excluded. Withdrawals are shared across this user’s personal links.
      </p>
      {balance.balanceShortfall > 0 && <Alert type="error" showIcon title="Reconciliation shortfall"
        description={`${amount(balance.balanceShortfall)} in withdrawals/refunds exceeds credited funds. The withdrawable balance is zero; investigate the underlying transactions.`}/>}
    </div>
  );
}

export function LinkPaymentBalance({link}: {link: any}) {
  return (
    <div>
      <Descriptions bordered size="small" column={{xs: 1, md: 2, xl: 3}}>
        <Descriptions.Item label="Book balance / paid collections">{amount(link.bookBalance)}</Descriptions.Item>
        <Descriptions.Item label="Active balance before pooled withdrawals">{amount(link.activeBalance)}</Descriptions.Item>
        <Descriptions.Item label="Restored failed payments">{amount(link.adminBalanceRefundAmount)}</Descriptions.Item>
        <Descriptions.Item label="Pending refunds">{amount(link.pendingRefundAmount)}</Descriptions.Item>
        <Descriptions.Item label="Completed refunds">{amount(link.refundedAmount)}</Descriptions.Item>
        <Descriptions.Item label="Paid / total attempts">{link.paidPaymentCount ?? "—"} / {link.totalPaymentCount ?? "—"}</Descriptions.Item>
      </Descriptions>
      <p className="mt-2 text-sm text-slate-500">
        This link’s active total is paid collections plus restored payments, less its refunds, before shared withdrawals.
        Use the user’s withdrawable balance above for funds available to withdraw.
        {link.totalPaymentCount > (link.payments?.length || 0) && ` Showing the latest ${link.payments?.length || 0} of ${link.totalPaymentCount} attempts; totals include all attempts.`}
      </p>
    </div>
  );
}
